export const COINS_TO_USD = 1000;
export const COINS_TO_INR = 12.5;

export const INVOICE_STATUS_OPTIONS = [
	{ value: "pending", label: "Pending", color: "#F59E0B" },
	{ value: "draft", label: "Draft", color: "#6c757d" },
	{ value: "sent", label: "Sent", color: "#007bff" },
	{ value: "partial", label: "Partial", color: "#ffc107" },
	{ value: "paid", label: "Paid", color: "#28a745" },
	{ value: "overdue", label: "Overdue", color: "#dc3545" },
	{ value: "reconciled", label: "Reconciled", color: "#17a2b8" },
	{ value: "cancelled", label: "Cancelled", color: "#6c757d" },
];

const INVOICED_STATUS_VALUES = new Set(["sent", "paid", "partial", "overdue", "draft"]);

export const USER_CHARGEBACK_TOOLTIP =
	"Coins/amount deducted from users on chargeback";

export const REVENUE_CHARGEBACK_TOOLTIP =
	"Our revenue lost on chargeback; uses partner-reported revenue when available";

export const NET_REVENUE_TOOLTIP =
	"Our net revenue from stats — ProRewards gross minus revenue chargeback (not partner invoice CB)";

export const PARTNER_INVOICE_CB_TOOLTIP =
	"Partner-reported chargebacks on invoice — info only, not deducted from final amount";

export const GROSS_INVOICE_AMOUNT_TOOLTIP =
	"Gross invoice amount in partner native currency";

export const BADGE_COLORS = {
	gray: { backgroundColor: "#F3F4F6", color: "#9CA3AF" },
	amber: { backgroundColor: "#FEF3C7", color: "#D97706" },
	orange: { backgroundColor: "#FFEDD5", color: "#EA580C" },
	green: { backgroundColor: "#D1FAE5", color: "#047857" },
};

/** @param {number | null | undefined} value */
export const formatUSD = (value) =>
	`$${(value ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** @param {number | null | undefined} value */
export const formatINR = (value) =>
	`₹${(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** @param {number | null | undefined} value @param {"INR" | "USD"} currency */
export const formatCurrency = (value, currency = "USD") =>
	currency === "INR" ? formatINR(value) : formatUSD(value);

/** @param {number | null | undefined} coins */
export const formatCoins = (coins) => {
	const value = coins ?? 0;
	if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M coins`;
	if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K coins`;
	return `${value.toLocaleString()} coins`;
};

/** @param {{ currency?: string, ourRevenueNative?: number } | null | undefined} partner */
export const formatNative = (partner, field = "ourRevenueNative") =>
	formatCurrency(partner?.[field] ?? 0, partner?.currency === "INR" ? "INR" : "USD");

/** @param {number | null | undefined} coins */
export const coinsToINR = (coins) => (coins ?? 0) / COINS_TO_INR;

/** @param {number | null | undefined} coins */
export const coinsToUSD = (coins) => (coins ?? 0) / COINS_TO_USD;

/** @param {number | null | undefined} received @param {number | null | undefined} gross */
export const getReceivedPercentage = (received, gross) => {
	const paid = received ?? 0;
	const invoiced = gross ?? 0;
	if (invoiced <= 0) return paid > 0 ? 100 : 0;
	return Number(((paid / invoiced) * 100).toFixed(1));
};

/** @param {string | undefined} month YYYY-MM */
export const formatMonthLabel = (month) => {
	if (!month || !/^\d{4}-\d{2}$/.test(month)) return "";
	const [year, m] = month.split("-");
	const date = new Date(Number(year), Number(m) - 1, 1);
	return date.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

/** @param {number} futureMonths */
export const generateMonthOptions = (futureMonths = 0) => {
	const options = [];
	const now = new Date();

	const addMonth = (offset) => {
		const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		options.push({
			value: `${year}-${month}`,
			label: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
		});
	};

	addMonth(0);
	for (let i = 1; i <= futureMonths; i++) addMonth(i);
	for (let i = 1; i < 12; i++) addMonth(-i);

	return options;
};

/** @param {string} startMonth @param {string} endMonth YYYY-MM */
export const countMonthsInRange = (startMonth, endMonth) => {
	const [sy, sm] = startMonth.split("-").map(Number);
	const [ey, em] = endMonth.split("-").map(Number);
	return (ey - sy) * 12 + (em - sm) + 1;
};

/** @param {string | undefined} startMonth @param {string | undefined} endMonth */
export const formatMonthRangeLabel = (startMonth, endMonth) => {
	if (!startMonth || !endMonth) return "";
	if (startMonth === endMonth) return formatMonthLabel(startMonth);
	return `${formatMonthLabel(startMonth)} – ${formatMonthLabel(endMonth)}`;
};

/** @param {string | undefined} startMonth @param {string | undefined} endMonth */
export const validateMonthRange = (startMonth, endMonth) => {
	if (!startMonth || !endMonth) {
		return { valid: false, error: "Select start and end month" };
	}
	if (!/^\d{4}-\d{2}$/.test(startMonth) || !/^\d{4}-\d{2}$/.test(endMonth)) {
		return { valid: false, error: "Invalid month format" };
	}
	if (startMonth > endMonth) {
		return { valid: false, error: "Start month must be before end month" };
	}
	const monthCount = countMonthsInRange(startMonth, endMonth);
	if (monthCount > 12) {
		return { valid: false, error: "Maximum 12 months allowed" };
	}
	return { valid: true, monthCount };
};

/** @returns {string} YYYY-MM */
export const getCurrentMonthYYYYMM = () => {
	const now = new Date();
	return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

/** @param {string | undefined} month */
export const isCurrentMonth = (month) => month === getCurrentMonthYYYYMM();

/** @param {Record<string, unknown> | null | undefined} partner */
export const getPartnerInvoice = (partner) => partner?.invoice ?? {};

/** @param {Record<string, unknown> | null | undefined} partner */
export const getInvoiceCurrency = (partner) => {
	const inv = getPartnerInvoice(partner);
	return inv.currency === "INR" || inv.currency === "USD" ? inv.currency : (partner?.currency === "INR" ? "INR" : "USD");
};

/** Canonical keys inside partner.invoice (see revenue-summary API). */
export const INVOICE_FIELDS = {
	reported: "prorewardsReportedNative",
	chargebacks: "chargebacksNative",
	gross: "grossNative",
	received: "receivedNative",
	outstanding: "outstandingNative",
	otherApps: "otherAppsRevenueNative",
	totalReported: "totalReportedNative",
};

/** @param {Record<string, unknown> | null | undefined} partner @param {string} field */
export const getInvoiceNative = (partner, field) => getPartnerInvoice(partner)[field] ?? 0;

/** @param {Record<string, unknown> | null | undefined} partner */
export const partnerRequiresInvoice = (partner) => partner?.requiresInvoice !== false;

/** @param {Record<string, unknown> | null | undefined} partner */
export const canCreateInvoice = (partner) => {
	if (!partnerRequiresInvoice(partner)) return false;
	const inv = getPartnerInvoice(partner);
	switch (inv.invoiceEligibility) {
		case "not_applicable":
		case "below_threshold":
		case "no_revenue":
		case "invoiced":
			return false;
		case "awaiting_partner":
			return true;
		default:
			return partnerRequiresInvoice(partner) && inv.invoiceUploaded !== true;
	}
};

/** @param {Record<string, unknown> | null | undefined} partner */
export const shouldShowPartnerDueDate = (partner) => getInvoiceBadge(partner).showDueDate;

/** @param {Record<string, unknown> | null | undefined} partner */
export const shouldShowInvoiceAmounts = (partner) => {
	if (!partnerRequiresInvoice(partner)) return false;
	const inv = getPartnerInvoice(partner);
	switch (inv.invoiceEligibility) {
		case "not_applicable":
		case "below_threshold":
		case "no_revenue":
			return false;
		default:
			return true;
	}
};

/** @param {Record<string, unknown> | null | undefined} partner @param {string | undefined} selectedMonth */
export const isPartnerAwaitingInvoice = (partner, selectedMonth) => {
	if (isCurrentMonth(selectedMonth)) return false;
	const inv = getPartnerInvoice(partner);
	if (inv.invoiceEligibility) {
		return inv.invoiceEligibility === "awaiting_partner";
	}
	return (
		partnerRequiresInvoice(partner) &&
		(partner?.netRevenueCoins ?? 0) > 0 &&
		inv.invoiceUploaded !== true
	);
};

/** @deprecated use isPartnerAwaitingInvoice */
export const isMissingInvoice = isPartnerAwaitingInvoice;

/** @param {string | string[] | null | undefined} invoicePDF */
export const getFirstInvoicePDF = (invoicePDF) => {
	if (!invoicePDF) return null;
	if (Array.isArray(invoicePDF)) return invoicePDF[0] || null;
	return invoicePDF;
};

/** @param {string | null | undefined} status */
export const getInvoiceStatusBadge = (status) => {
	const statusObj = INVOICE_STATUS_OPTIONS.find((s) => s.value === status) || INVOICE_STATUS_OPTIONS[0];
	return { label: statusObj.label, color: statusObj.color };
};

/** @param {Record<string, unknown> | null | undefined} partner */
export const getInvoiceBadge = (partner) => {
	const inv = getPartnerInvoice(partner);

	if (!partnerRequiresInvoice(partner) || inv.invoiceEligibility === "not_applicable") {
		return { label: "N/A", ...BADGE_COLORS.gray, showDueDate: false, isLink: false, tooltip: "No invoice required" };
	}

	switch (inv.invoiceEligibility) {
		case "no_revenue":
			return { label: "—", ...BADGE_COLORS.gray, showDueDate: false, isLink: false };
		case "below_threshold":
			return {
				label: "Below Threshold",
				tooltip: "Cumulative revenue under invoice minimum — invoice not required yet",
				...BADGE_COLORS.gray,
				showDueDate: false,
				isLink: false,
			};
		case "awaiting_partner":
			return {
				label: "Awaiting Invoice",
				...BADGE_COLORS.amber,
				showDueDate: false,
				isLink: false,
			};
		case "invoiced": {
			const status = INVOICED_STATUS_VALUES.has(inv.invoiceStatus)
				? inv.invoiceStatus
				: "sent";
			const statusBadge = getInvoiceStatusBadge(status);
			const pdfUrl = inv.invoiceUploaded ? getFirstInvoicePDF(inv.invoicePDF) : null;
			return {
				label: statusBadge.label,
				backgroundColor: `${statusBadge.color}22`,
				color: statusBadge.color,
				showDueDate: true,
				isLink: !!pdfUrl,
				href: pdfUrl,
				tooltip: pdfUrl ? "View invoice PDF" : undefined,
			};
		}
		default:
			break;
	}

	if ((partner?.netRevenueCoins ?? 0) <= 0) {
		return { label: "—", ...BADGE_COLORS.gray, showDueDate: false, isLink: false };
	}
	if (inv.invoiceUploaded) {
		const pdfUrl = getFirstInvoicePDF(inv.invoicePDF);
		const statusBadge = getInvoiceStatusBadge(inv.invoiceStatus);
		return {
			label: statusBadge.label || "Uploaded",
			backgroundColor: `${statusBadge.color}22`,
			color: statusBadge.color,
			showDueDate: true,
			isLink: !!pdfUrl,
			href: pdfUrl,
		};
	}
	return {
		label: "Awaiting Invoice",
		...BADGE_COLORS.amber,
		showDueDate: false,
		isLink: false,
	};
};

/** @param {number | null | undefined} rate */
export const formatFxRate = (rate) => {
	if (rate == null || rate === 0) return "—";
	return Number(rate).toLocaleString("en-IN", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
	});
};

/** @param {string | null | undefined} date */
export const formatDueDate = (date) => {
	if (!date) return "—";
	const parsed = new Date(date);
	if (Number.isNaN(parsed.getTime())) return "—";
	return parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/** @param {Record<string, unknown> | null | undefined} partner */
export const getPartnerDueDate = (partner) => {
	const badge = getInvoiceBadge(partner);
	if (!badge.showDueDate) return "—";
	return formatDueDate(getPartnerInvoice(partner).dueDate);
};

/** @param {Record<string, unknown> | null | undefined} partner */
export const getPartnerNetReceived = (partner) => getInvoiceNative(partner, INVOICE_FIELDS.received);

/** @param {Record<string, unknown> | null | undefined} partner */
export const getPartnerGrossAmount = (partner) => getInvoiceNative(partner, INVOICE_FIELDS.gross);

/** @param {Record<string, unknown> | null | undefined} partner */
export const getPartnerThresholdProgress = (partner) =>
	getPartnerInvoice(partner).thresholdProgress || partner?.thresholdProgress || null;

export const getCurrencyBadgeStyle = (currency) =>
	currency === "INR"
		? { backgroundColor: "#D1FAE5", color: "#047857" }
		: { backgroundColor: "#DBEAFE", color: "#1D4ED8" };
